import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Box3, CanvasTexture, CatmullRomCurve3, Group, SRGBColorSpace, Vector3 } from "three";
import { Clone } from "@react-three/drei";
import { useOptionalGLTF } from "../scenes/sede/materials";
import { useVehicleStore } from "../state/vehicleSlice";
import { CAR_TRACK, BRAND_GREEN } from "../scenes/sede/layout";

/**
 * Decal de texto plano para el vehículo (canvas transparente, texto sobrio
 * en verde #006B3F). La rotación correcta por lado evita el espejado.
 */
function TextDecal({
  text,
  width,
  height,
  position,
  rotation = [0, 0, 0] as [number, number, number],
  fontScale = 0.55,
}: {
  text: string;
  width: number;
  height: number;
  position: [number, number, number];
  rotation?: [number, number, number];
  fontScale?: number;
}) {
  const texture = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = Math.max(64, Math.round((512 * height) / width));
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = BRAND_GREEN;
    ctx.font = `700 ${canvas.height * fontScale}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    const tex = new CanvasTexture(canvas);
    tex.colorSpace = SRGBColorSpace;
    tex.anisotropy = 4;
    return tex;
  }, [text, width, height, fontScale]);
  return (
    <mesh position={position} rotation={rotation}>
      <planeGeometry args={[width, height]} />
      <meshStandardMaterial map={texture} transparent roughness={0.5} polygonOffset polygonOffsetFactor={-1} />
    </mesh>
  );
}

/**
 * Rótulos reglamentarios del vehículo de examen
 * [NORMATIVO Anexo 60, num. 2.2.1 y 2.2.4–2.2.5]:
 * "PRUEBA" verde Pantone 3425 en ambas puertas y "CALE" pequeño en el capó.
 */
function CarDecals({ halfWidth, bodyY }: { halfWidth: number; bodyY: number }) {
  return (
    <group>
      {/* PRUEBA en ambas puertas (cada lado con su orientación, sin espejar) */}
      <TextDecal text="PRUEBA" width={1.15} height={0.3} position={[halfWidth + 0.012, bodyY, 0.1]} rotation={[0, Math.PI / 2, 0]} />
      <TextDecal text="PRUEBA" width={1.15} height={0.3} position={[-halfWidth - 0.012, bodyY, 0.1]} rotation={[0, -Math.PI / 2, 0]} />
      {/* CALE pequeño en el capó, legible desde el frente */}
      <TextDecal text="CALE" width={0.7} height={0.22} position={[0, bodyY + 0.2, 1.55]} rotation={[-Math.PI / 2 + 0.12, 0, 0]} fontScale={0.6} />
    </group>
  );
}

/**
 * Banderín verde de 1.5 m de la motocicleta de examen
 * [NORMATIVO Anexo 60, num. 2.2.5]: asta + banderín triangular.
 */
function MotoPennant() {
  return (
    <group position={[0, 0, -0.75]}>
      <mesh position={[0, 0.75, 0]} castShadow>
        <cylinderGeometry args={[0.012, 0.012, 1.5, 6]} />
        <meshStandardMaterial color="#3a3f42" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh position={[0.16, 1.36, 0]}>
        <coneGeometry args={[0.16, 0.34, 3]} />
        <meshStandardMaterial color={BRAND_GREEN} roughness={0.55} side={2} />
      </mesh>
    </group>
  );
}

interface VehicleProps {
  kind: "car" | "moto";
  position?: [number, number, number];
  rotationY?: number;
  /** true: el vehículo sigue la trayectoria controlada de la pista (pista-auto). */
  track?: boolean;
  /** Rezago en la trayectoria (0-1): el auto escolta sigue al de examen. */
  progressOffset?: number;
}

/**
 * Vehículo de examen: blanco con franja verde institucional (#006B3F,
 * aprox. Pantone 3425), rótulo "PRUEBA" en ambas puertas y "CALE" en el
 * capó; la moto lleva banderín verde de 1.5 m. Sin placas [DISEÑO].
 * Auto ~4.4 m, moto ~2.1 m (normalizados por bbox). Intenta cargar
 * auto.glb/moto.glb; si no existen, usa un placeholder procedural.
 *
 * Con `track`, el auto recorre una polyline predefinida (CatmullRomCurve3)
 * controlada desde vehicleSlice (avanzar/detener/retroceder/reiniciar):
 * trayectoria educativa, NO simulador libre.
 */
export function Vehicle({ kind, position = [0, 0, 0], rotationY = 0, track = false, progressOffset = 0 }: VehicleProps) {
  const url = kind === "car" ? "/assets/models/auto.glb" : "/assets/models/moto.glb";
  // Los GLB vienen con el eje largo en X (auto 1.9 m, moto 1.9 m); se normaliza
  // por el eje mayor y se rota el modelo para que la nariz quede en +Z, que es
  // la convención de la trayectoria (atan2(tan.x, tan.z)) y de los rótulos.
  const { scene, loading } = useOptionalGLTF(url, {
    target: kind === "car" ? 4.4 : 2.1,
    axis: "max",
  });
  const groupRef = useRef<Group>(null);

  // Altura de apoyo y ancho de carrocería tras la rotación interna: el ancho
  // en X del conjunto corresponde al eje Z original del modelo.
  const { groundOffset, halfWidth } = useMemo(() => {
    if (!scene) return { groundOffset: 0, halfWidth: 0.9 };
    const bbox = new Box3().setFromObject(scene);
    const size = bbox.getSize(new Vector3());
    return {
      groundOffset: Number.isFinite(bbox.min.y) ? -bbox.min.y : 0,
      halfWidth: size.z / 2,
    };
  }, [scene]);

  const curve = useMemo(() => {
    if (!track) return null;
    const { x, z, w, d } = CAR_TRACK;
    return new CatmullRomCurve3(
      [
        new Vector3(x - w / 2 + 3, 0, z),
        new Vector3(x - w / 4, 0, z - d / 4),
        new Vector3(x, 0, z + d / 4),
        new Vector3(x + w / 4, 0, z - d / 4),
        new Vector3(x + w / 2 - 8, 0, z),
        new Vector3(x + w / 2 - 9, 0, z - d / 2 + 5),
      ],
      false,
      "catmullrom",
      0.3,
    );
  }, [track]);
  const pathLength = useMemo(() => curve?.getLength() ?? 0, [curve]);

  useFrame((_, delta) => {
    if (!track || !curve || !groupRef.current) return;
    const store = useVehicleStore.getState();
    if (progressOffset === 0) store.tick(delta, pathLength);
    const { progress } = useVehicleStore.getState();
    const p = curve.getPointAt(Math.min(Math.max(progress - progressOffset, 0), 1));
    const tan = curve.getTangentAt(Math.min(Math.max(progress - progressOffset, 0), 1));
    groupRef.current.position.set(p.x, 0, p.z);
    groupRef.current.rotation.y = Math.atan2(tan.x, tan.z);
    // el escolta no se monta sobre el vehículo de examen en la salida
    if (progressOffset > 0) groupRef.current.visible = progress > progressOffset;
  });

  const isGltf = !loading && scene;
  return (
    <group ref={groupRef} position={position} rotation={[0, rotationY, 0]} name={`vehiculo-${kind}`}>
      {isGltf ? (
        <group position={[0, groundOffset, 0]} rotation={[0, -Math.PI / 2, 0]}>
          <Clone object={scene as Group} castShadow receiveShadow />
        </group>
      ) : kind === "car" ? (
        <CarPlaceholder />
      ) : (
        <MotoPlaceholder />
      )}
      {/* rótulos y banderín reglamentarios (GLB y placeholder por igual);
          altura de puerta ~0.78 m sobre el piso una vez apoyadas las ruedas */}
      {kind === "car" && <CarDecals halfWidth={isGltf ? halfWidth : 0.91} bodyY={isGltf ? 0.78 : 0.68} />}
      {kind === "moto" && <MotoPennant />}
    </group>
  );
}

/** Auto de examen (~4.4 × 1.8 × 1.5 m), blanco con franja verde. */
function CarPlaceholder() {
  return (
    <group>
      {/* carrocería */}
      <mesh position={[0, 0.55, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.8, 0.55, 4.4]} />
        <meshStandardMaterial color="#f2f2ef" roughness={0.35} metalness={0.2} />
      </mesh>
      {/* cabina */}
      <mesh position={[0, 1.05, 0.2]} castShadow>
        <boxGeometry args={[1.6, 0.5, 2.4]} />
        <meshStandardMaterial color="#dfe4e6" roughness={0.15} metalness={0.3} />
      </mesh>
      {/* franja verde institucional en los costados */}
      {[-0.92, 0.92].map((x) => (
        <mesh key={x} position={[x, 0.62, 0]}>
          <boxGeometry args={[0.02, 0.22, 4.2]} />
          <meshStandardMaterial color={BRAND_GREEN} roughness={0.45} />
        </mesh>
      ))}
      {/* ruedas */}
      {[
        [-0.85, 1.4],
        [0.85, 1.4],
        [-0.85, -1.4],
        [0.85, -1.4],
      ].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.33, z]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.33, 0.33, 0.22, 16]} />
          <meshStandardMaterial color="#1c1f21" roughness={0.9} />
        </mesh>
      ))}
      {/* luces */}
      <mesh position={[0, 0.62, 2.21]}>
        <boxGeometry args={[1.5, 0.14, 0.04]} />
        <meshStandardMaterial color="#fff6d8" emissive="#fff2c0" emissiveIntensity={0.6} />
      </mesh>
    </group>
  );
}

/** Moto de examen (~2.1 m), blanca con franja verde. */
function MotoPlaceholder() {
  return (
    <group>
      {/* ruedas */}
      {[0.85, -0.75].map((z, i) => (
        <mesh key={i} position={[0, 0.33, z]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.33, 0.33, 0.12, 14]} />
          <meshStandardMaterial color="#1c1f21" roughness={0.9} />
        </mesh>
      ))}
      {/* chasis */}
      <mesh position={[0, 0.62, 0.05]} rotation={[0.15, 0, 0]} castShadow>
        <boxGeometry args={[0.22, 0.28, 1.3]} />
        <meshStandardMaterial color="#f2f2ef" roughness={0.35} metalness={0.25} />
      </mesh>
      {/* tanque con franja verde */}
      <mesh position={[0, 0.82, 0.15]} castShadow>
        <boxGeometry args={[0.26, 0.18, 0.5]} />
        <meshStandardMaterial color={BRAND_GREEN} roughness={0.45} />
      </mesh>
      {/* asiento */}
      <mesh position={[0, 0.82, -0.35]}>
        <boxGeometry args={[0.24, 0.08, 0.55]} />
        <meshStandardMaterial color="#26292b" roughness={0.8} />
      </mesh>
      {/* manubrio */}
      <mesh position={[0, 1.02, 0.75]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.025, 0.025, 0.6, 8]} />
        <meshStandardMaterial color="#3a4145" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh position={[0, 0.78, 0.8]} rotation={[0.4, 0, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 0.6, 8]} />
        <meshStandardMaterial color="#3a4145" metalness={0.6} roughness={0.4} />
      </mesh>
    </group>
  );
}
